import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, ShieldCheck, UserCog } from 'lucide-react';
import { api, ApiError } from '../api';
import type { UserSummary } from '../api';
import { AppShell } from '../ui/AppShell';
import { PageHeader } from '../ui/PageHeader';
import { SectionCard } from '../ui/components';
import { ActionQueueCard, MetricStrip } from '../ui/mobileOps';
import {
  Button,
  EmptyState,
  ErrorBanner,
  Pill,
  Spinner,
  TextInput,
} from '../ui/primitives';

export function UsersAdminPage() {
  const qc = useQueryClient();
  const usersQ = useQuery<UserSummary[]>({
    queryKey: ['users'],
    queryFn: () => api.get<UserSummary[]>('/api/users'),
  });
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [lastResetInfo, setLastResetInfo] = useState<{
    username: string;
    newPassword: string;
  } | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      api.post<UserSummary>('/api/users', {
        username: newUsername,
        password: newPassword,
      }),
    onSuccess: () => {
      setNewUsername('');
      setNewPassword('');
      setError(null);
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/users/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const resetMut = useMutation({
    mutationFn: (u: UserSummary) => api.post<{ newPassword: string }>(`/api/users/${u.id}/reset-password`),
    onSuccess: (res, u) => {
      setLastResetInfo({ username: u.username, newPassword: res.newPassword });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const stats = useMemo(() => {
    const users = usersQ.data ?? [];
    return {
      total: users.length,
      bosses: users.filter((user) => user.role === 'boss').length,
      pinReady: users.filter((user) => user.hasPin).length,
    };
  }, [usersQ.data]);

  return (
    <AppShell
      title="Users"
      toolbar={
        <PageHeader
          section="settings"
          title="Users"
          subtitle="Owner-only access control for boss accounts."
          breadcrumb={[{ label: 'Settings', to: '/settings' }, { label: 'Users' }]}
        />
      }
    >
      <div className="space-y-4">
        <MetricStrip
          section="settings"
          items={[
            { label: 'Users', value: String(stats.total), note: 'owner + boss accounts' },
            { label: 'Boss users', value: String(stats.bosses), note: 'read-only oversight logins' },
            { label: 'PIN ready', value: `${stats.pinReady}/${stats.total || 0}`, note: 'users with a PIN set', tone: stats.total > 0 && stats.pinReady === stats.total ? 'pos' : 'warn' },
          ]}
        />

        <div className="grid gap-4 lg:grid-cols-[0.96fr_1.04fr]">
          <ActionQueueCard
            section="settings"
            eyebrow="Access control"
            title="Create or recover boss users without opening the database"
            description="Boss users can review every desk in the app while all data-changing actions stay blocked. Use password reset if someone loses access."
            meta={
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <span>Owner account</span>
                  <span className="font-medium text-ink">Always one</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Boss users</span>
                  <span className="font-medium text-ink">Safe for oversight</span>
                </div>
                <div className="text-[12px] leading-5 text-ink-muted">
                  Password resets generate a one-time password. Share it privately and ask the user to change it immediately after login.
                </div>
              </div>
            }
          />

          <SectionCard section="settings" title="Add boss user">
            <p className="mb-4 text-[13px] leading-5 text-ink-muted">
              This creates a read-only login for oversight. Boss users can inspect ledgers, batches, cash, and match flows without being able to mutate data.
            </p>
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                createMut.mutate();
              }}
            >
              <TextInput
                label="Username"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                name="new-user-username"
              />
              <TextInput
                label="Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                name="new-user-password"
                hint="Minimum 10 characters."
              />
              {error && <ErrorBanner>{error}</ErrorBanner>}
              <Button type="submit" disabled={createMut.isPending}>
                {createMut.isPending ? <Spinner /> : 'Create boss user'}
              </Button>
            </form>
          </SectionCard>
        </div>

        {lastResetInfo && (
          <ActionQueueCard
            section="settings"
            eyebrow="One-time password"
            title={`New password ready for ${lastResetInfo.username}`}
            description="Share this password securely. Existing sessions were revoked. The user should sign in and change it immediately."
            tone="warn"
            meta={<code className="block break-all font-mono text-small text-ink">{lastResetInfo.newPassword}</code>}
            actions={
              <Button size="sm" variant="secondary" onClick={() => setLastResetInfo(null)}>
                Dismiss
              </Button>
            }
          />
        )}

        <SectionCard section="settings" title="Existing users">
          {usersQ.isLoading ? (
            <div className="flex items-center gap-2 text-caption text-ink-muted">
              <Spinner /> loading...
            </div>
          ) : usersQ.data && usersQ.data.length > 0 ? (
            <div className="space-y-3">
              {usersQ.data.map((user) => {
                const resetting = resetMut.isPending && resetMut.variables?.id === user.id;
                const deleting = deleteMut.isPending && deleteMut.variables === user.id;
                const ownerRow = user.role === 'owner';
                return (
                  <div
                    key={user.id}
                    className="rounded-[24px] border border-border/70 app-shell-surface p-4 shadow-sm backdrop-blur"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-small font-semibold tracking-[-0.01em] text-ink">{user.username}</div>
                          <Pill tone={ownerRow ? 'amber' : 'neutral'}>{user.role}</Pill>
                          <Pill tone={user.hasPin ? 'green' : 'amber'}>{user.hasPin ? 'PIN set' : 'No PIN'}</Pill>
                        </div>
                        <div className="mt-2 text-[12px] leading-5 text-ink-muted">
                          Created {new Date(user.createdAt).toLocaleDateString()}.
                          {ownerRow
                            ? ' The owner account cannot be deleted here.'
                            : ' This login can review the business without editing it.'}
                        </div>
                      </div>
                      {!ownerRow && (
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              if (confirm(`Reset password for ${user.username}?`)) resetMut.mutate(user);
                            }}
                            disabled={resetting}
                          >
                            {resetting ? <Spinner /> : 'Reset password'}
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              if (confirm(`Delete user ${user.username}?`)) deleteMut.mutate(user.id);
                            }}
                            disabled={deleting}
                          >
                            {deleting ? <Spinner /> : 'Delete'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              section="settings"
              icon={UserCog}
              title="No extra users yet"
              description="Create a boss user if someone needs oversight access without edit rights."
            />
          )}
        </SectionCard>
      </div>
    </AppShell>
  );
}
